// EVA-303: display rules specific to the Value Bridge tab.
//
// Pure ESM so spec/javascript can probe it — the components own markup only.
//
// The en-GB grouping, the em dash for "unavailable" and the whole/percent/date
// formatters are NOT redefined here: they are imported from
// `valuation_summary/format.js`, which already owns them and which
// FloatingValueConclusion already imports across features. EVA-266 was a whole
// ticket spent deleting near-duplicates of exactly that module.
//
// What IS new is the movement vocabulary, and one rule separates it from every
// other tab: A ZERO IS A FINDING HERE. `formatMoneyWhole` in
// utils/reportFormat.js renders 0 as a dash because a report row of zero is
// noise; on this tab "this driver changed and moved the valuation by nothing"
// is exactly the answer the valuer came for. nil is still unavailable and still
// never a zero (money-and-dates.md).

import { EM_DASH, formatIsoDate, formatWhole, toNumber } from '../valuation_summary/format.js'

export { EM_DASH, formatIsoDate, formatWhole }

const TWO_DP = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})
const WHOLE = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })
const DETAIL_NUMBER = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 4 })
const CURRENCY_FORMATTERS = new Map()
const DETAIL_CURRENCY_FORMATTERS = new Map()

// ROUND_HALF_UP means the MAGNITUDE rounds away from zero (money-and-dates.md).
// `Math.round` is half-toward-positive-infinity, so it would send -1.5 to -1
// while `formatWhole`'s Intl sends it to -2 — two formatters on one screen
// disagreeing about the same number.
function roundHalfUp(value) {
  return Math.sign(value) * Math.round(Math.abs(value))
}

// A movement carries its sign explicitly — the reader is being told a
// direction, not just a size. Rounded BEFORE the sign test, so a movement of
// -0.4 reads as "0" rather than as "-0": the display is a whole-pound statement
// and a minus sign in front of nothing claims a fall the figures do not
// support.
export function formatMovement(value) {
  const parsed = toNumber(value)
  if (parsed === null) return EM_DASH

  const rounded = roundHalfUp(parsed)
  if (rounded === 0) return WHOLE.format(0)
  return `${rounded > 0 ? '+' : '-'}${WHOLE.format(Math.abs(rounded))}`
}

function currencyFormatter(currency) {
  const code = String(currency || '').trim().toUpperCase()
  if (!code) return null
  if (CURRENCY_FORMATTERS.has(code)) return CURRENCY_FORMATTERS.get(code)

  try {
    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0
    })
    CURRENCY_FORMATTERS.set(code, formatter)
    return formatter
  } catch {
    return null
  }
}

// Driver Analysis no longer has a table header to carry the unit, so every
// money figure carries it directly. Values are rounded before Intl sees them:
// that keeps the same ROUND_HALF_UP contract as formatMovement and avoids
// delegating a financial rounding decision to browser defaults.
export function formatMoney(value, currency) {
  const parsed = toNumber(value)
  if (parsed === null) return EM_DASH

  const rounded = roundHalfUp(parsed)
  const formatter = currencyFormatter(currency)
  if (formatter) return formatter.format(rounded)

  const code = String(currency || '').trim().toUpperCase()
  return `${code ? `${code} ` : ''}${WHOLE.format(rounded)}`
}

export function formatMoneyMovement(value, currency) {
  const parsed = toNumber(value)
  if (parsed === null) return EM_DASH

  const rounded = roundHalfUp(parsed)
  if (rounded === 0) return formatMoney(0, currency)
  return `${rounded > 0 ? '+' : '-'}${formatMoney(Math.abs(rounded), currency)}`
}

function detailCurrencyFormatter(currency) {
  const code = String(currency || '').trim().toUpperCase()
  if (!code) return null
  if (DETAIL_CURRENCY_FORMATTERS.has(code)) return DETAIL_CURRENCY_FORMATTERS.get(code)

  try {
    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    })
    DETAIL_CURRENCY_FORMATTERS.set(code, formatter)
    return formatter
  } catch {
    return null
  }
}

function formatDetailMoney(value, currency) {
  const parsed = toNumber(value)
  if (parsed === null) return EM_DASH

  const formatter = detailCurrencyFormatter(currency)
  if (formatter) return formatter.format(parsed)

  const code = String(currency || '').trim().toUpperCase()
  return `${code ? `${code} ` : ''}${TWO_DP.format(parsed)}`
}

function detailUnit(input, areaInput, resolveLabel) {
  const unit = String(input || '').trim().toLowerCase()
  if (!unit || typeof resolveLabel !== 'function') return ''

  const areaUnit = String(areaInput || '').trim().toLowerCase()
  const vars = areaUnit ? { area_unit: resolveLabel(`inputs.units.${areaUnit}`) } : {}
  return resolveLabel(`inputs.units.${unit}`, vars)
}

export function formatChangedInputValue(input, side, currency, resolveLabel) {
  const value = input?.[side]
  if (value == null || value === '') return EM_DASH
  const labelKey = input?.[`${side}_value_label_key`]
  if (labelKey) return resolveLabel(`inputs.values.${labelKey}`)
  const valueKind = input?.[`${side}_value_kind`] ?? input?.value_kind
  const valueUnit = input?.[`${side}_value_unit`] ?? input?.value_unit
  const areaUnit = input?.[`${side}_area_unit`] ?? input?.area_unit

  switch (valueKind) {
    case 'percent': {
      const parsed = toNumber(value)
      return parsed === null ? EM_DASH : `${TWO_DP.format(parsed)}%`
    }
    case 'money': {
      const amount = formatDetailMoney(value, currency)
      const unit = detailUnit(valueUnit, areaUnit, resolveLabel)
      return unit ? `${amount} ${unit}` : amount
    }
    case 'date':
      return formatIsoDate(value)
    case 'duration_months':
    case 'duration_years': {
      const count = toNumber(value)
      if (count === null) return EM_DASH
      const key = valueKind === 'duration_months' ? 'inputs.units.months' : 'inputs.units.years'
      return resolveLabel(key, { count })
    }
    case 'months_rent': {
      const count = toNumber(value)
      return count === null ? EM_DASH : resolveLabel('inputs.units.months_rent', { count })
    }
    case 'number': {
      const number = toNumber(value)
      if (number === null) return EM_DASH
      const unit = detailUnit(valueUnit, areaUnit, resolveLabel)
      return unit ? `${DETAIL_NUMBER.format(number)} ${unit}` : DETAIL_NUMBER.format(number)
    }
    case 'boolean':
      return resolveLabel(`inputs.values.boolean.${String(value)}`)
    case 'list':
      return Array.isArray(value) && value.length > 0 ? value.join(', ') : EM_DASH
    default:
      return String(value)
  }
}

export function changedInputItem(input, resolveLabel) {
  if (input?.item_label) return input.item_label
  const key = input?.item_label_key
  return key && typeof resolveLabel === 'function'
    ? resolveLabel(`inputs.items.${key}`)
    : input?.model || EM_DASH
}

export function changedInputField(input, resolveLabel) {
  const key = input?.field_label_key || input?.column
  return key && typeof resolveLabel === 'function'
    ? resolveLabel(`inputs.labels.${key}`)
    : EM_DASH
}

export function changedInputSections(inputs) {
  const annual = (inputs || []).filter((input) => input?.kind === 'annual_series')
  const schedules = (inputs || []).filter((input) => input?.kind === 'schedule')
  const values = (inputs || []).filter((input) => ![ 'annual_series', 'schedule' ].includes(input?.kind))

  return { annual, schedules, values }
}

export function annualSeriesCells(input, side) {
  return (input?.[side] || []).map((value) => {
    const parsed = toNumber(value)
    return parsed === null ? EM_DASH : `${TWO_DP.format(parsed)}%`
  })
}

function scheduleDescription(side, resolveLabel) {
  if (!side?.enabled) return resolveLabel('inputs.schedule.disabled')

  const keyFor = (value) => String(value || '')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/([0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
  const basis = keyFor(side.basis)
  const allocation = keyFor(side.allocation)
  const basisLabel = resolveLabel(`inputs.values.codes.${basis}`)
  if (!allocation) return basisLabel

  return resolveLabel('inputs.schedule.configuration', {
    basis: basisLabel,
    allocation: resolveLabel(`inputs.values.codes.${allocation}`)
  })
}

export function scheduleRows(input, currency, resolveLabel) {
  const earlier = input?.earlier || {}
  const later = input?.later || {}
  const byPeriod = new Map()

  ;[ [ 'earlier', earlier ], [ 'later', later ] ].forEach(([ side, payload ]) => {
    ;(payload.entries || []).forEach((entry) => {
      const key = `${entry.year}:${entry.month || 0}`
      const row = byPeriod.get(key) || { year: entry.year, month: entry.month || null }
      row[side] = formatDetailMoney(entry.value, currency)
      byPeriod.set(key, row)
    })
  })

  const amounts = Array.from(byPeriod.values())
    .sort((a, b) => (a.year - b.year) || ((a.month || 0) - (b.month || 0)))
    .map((row) => ({
      label: resolveLabel(row.month ? 'inputs.period.month' : 'inputs.period.year', row),
      earlier: row.earlier || EM_DASH,
      later: row.later || EM_DASH
    }))

  return [ {
    label: resolveLabel('inputs.schedule.configuration_label'),
    earlier: scheduleDescription(earlier, resolveLabel),
    later: scheduleDescription(later, resolveLabel)
  }, ...amounts ]
}

// Percent of opening, signed like the movement it belongs to. The server has
// already expressed it as a percentage, so this only rounds and signs it —
// rounding first, for the same reason as formatMovement above.
export function formatSignedPercent(value) {
  const parsed = toNumber(value)
  if (parsed === null) return EM_DASH

  const rounded = Math.sign(parsed) * Math.round(Math.abs(parsed) * 100) / 100
  if (rounded === 0) return `${TWO_DP.format(0)}%`
  return `${rounded > 0 ? '+' : '-'}${TWO_DP.format(Math.abs(rounded))}%`
}

// Shared semantic vocabulary for the chart and its largest-driver badge. The
// amount is a decimal string at the wire boundary; unavailable and rounded-away
// movements stay neutral rather than claiming a direction the figure cannot show.
export function movementTone(value) {
  const parsed = toNumber(value)
  if (parsed === null || parsed === 0) return 'neutral'
  return parsed > 0 ? 'positive' : 'negative'
}

// "MarketLeasingAssumption" -> "market_leasing_assumption", so a model name can
// address an i18n key without the component keeping a parallel list that goes
// stale the day a model is added.
function underscore(name) {
  return String(name || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
}

// "Tenant" + "lease_end_date" -> "Tenant · Lease end date". A DTO may
// supply a record identity and a translated domain field label when the raw
// class/column loses meaning (a rent-free window is stored as a lease break).
//
// The changed-inputs list is audit detail: which fields moved, so a
// disagreement can be traced. Naming all 149 owned columns in the locale file
// would be a catalogue that goes stale the day a column is added, and a stale
// entry there is worse than a humanised one — it would label the wrong field.
export function inputName(modelName, column, entityLabel, fieldLabel) {
  const field = String(column || '').replace(/_/g, ' ').trim()
  const humanised = field ? field.charAt(0).toUpperCase() + field.slice(1) : ''
  return [entityLabel || modelName, fieldLabel || humanised].filter(Boolean).join(' · ')
}

// Resolve the semantic field key at the same boundary that consumes the DTO.
// Keeping this outside JSX lets the Node probe cover the complete
// `field_label_key -> i18n path -> rendered name` contract.
export function changedInputName(input, resolveLabel) {
  const fieldKey = input?.field_label_key
  const fieldLabel = fieldKey && typeof resolveLabel === 'function'
    ? resolveLabel(`inputs.labels.${fieldKey}`)
    : null

  return inputName(input?.model, input?.column, input?.entity_label, fieldLabel)
}

function summaryValue(summary, side, resolveLabel) {
  const value = summary?.[side]
  if (value == null || value === '') return EM_DASH

  if (summary.value_kind === 'percent') {
    const parsed = toNumber(value)
    return parsed === null ? EM_DASH : `${TWO_DP.format(parsed)}%`
  }

  if (summary.value_kind === 'discounting_method') {
    return resolveLabel(`summaries.values.discounting_method.${String(value).toLowerCase()}`)
  }

  return String(value)
}

// EVA-352. The persisted result carries semantic summary descriptors, never
// English copy. Rendering them at the React/i18n boundary keeps user-visible
// wording in the locale catalogue while the server remains the source of truth
// for which full (uncapped) set of economic leaves changed.
export function economicSummary(summary, resolveLabel) {
  if (!summary || typeof resolveLabel !== 'function') return ''

  if (summary.kind === 'scalar') {
    return resolveLabel('summaries.scalar', {
      field: resolveLabel(`summaries.fields.${summary.field}`),
      earlier: summaryValue(summary, 'earlier', resolveLabel),
      later: summaryValue(summary, 'later', resolveLabel)
    })
  }

  if (summary.kind === 'changes') {
    return (summary.items || [])
      .map((item) => economicSummary(item, resolveLabel))
      .filter(Boolean)
      .join('; ')
  }

  if (summary.kind === 'growth_curve') {
    return resolveLabel('summaries.growth_curve', {
      name: summary.name || resolveLabel('summaries.unnamed_growth_curve')
    })
  }

  if (summary.kind === 'records') {
    const count = Number(summary.count) || 0
    return resolveLabel('summaries.records', {
      count,
      model: resolveLabel(`summaries.models.${summary.model || 'record'}`, { count })
    })
  }

  if (summary.kind === 'correspondence') {
    const earlier = Number(summary.earlier_count ?? summary.earlierCount) || 0
    const later = Number(summary.later_count ?? summary.laterCount) || 0
    const count = Math.max(earlier, later)
    return resolveLabel('summaries.correspondence', {
      earlier,
      later,
      model: resolveLabel(`summaries.models.${summary.model || 'record'}`, { count })
    })
  }

  if (summary.kind === 'inputs') {
    const count = Number(summary.count) || 0
    return resolveLabel('summaries.inputs', { count })
  }

  return ''
}

// Which translation key names a row, as { key, vars } for the caller to resolve
// through the translations prop. No user-visible string is hardcoded here.
//
// The vocabulary is deliberately NEUTRAL for a consolidated unit. The resolver
// could not correspond those records, and the model cannot know whether a lease
// was renewed, surrendered or replaced — so the label says the correspondence
// could not be established and stops there.
export function rowLabel(row) {
  if (!row) return { key: 'rows.unknown', vars: {} }

  switch (row.kind) {
    case 'time':
      return { key: 'rows.time', vars: {} }
    case 'residual':
      return { key: 'rows.residual', vars: {} }
    case 'rounding':
      return { key: 'rows.rounding', vars: {} }
    case 'consolidated':
      return { key: `consolidated.${underscore(row.model_name)}`, vars: {} }
    default:
      if (row.driver_id === 'discounting') {
        const items = row.summary?.kind === 'changes' ? row.summary.items : [ row.summary ]
        const fields = new Set((items || []).map((item) => item?.field).filter(Boolean))
        if (fields.has('discounting_method') && !fields.has('discount_rate')) {
          return { key: 'drivers.discounting_method', vars: {} }
        }
        if (fields.has('discounting_method') && fields.has('discount_rate')) {
          return { key: 'drivers.discounting_assumptions', vars: {} }
        }
      }
      return { key: `drivers.${row.driver_id}`, vars: {} }
  }
}

// The analysis sections: each group the server named, its subtotal, and the
// rows that subtotal is over.
//
// The ORDER and the MEMBERSHIP both come from the server, which emits one group
// per group that has rows, in the catalogue's order. Re-deriving either here
// would be a second opinion on the same question, and the serializer's subtotal
// is only trustworthy because it sums exactly these rows.
//
// A row whose group the server did not name is still rendered — under its own
// group with an unavailable subtotal — rather than dropped. Losing a movement
// because a group list went stale is the one failure this must not have.
export function groupSections(rows, groups) {
  const buckets = new Map()
  ;(rows || []).forEach((row) => {
    if (!buckets.has(row.group)) buckets.set(row.group, [])
    buckets.get(row.group).push(row)
  })

  const named = (groups || []).map((group) => {
    const sectionRows = buckets.get(group.id) || []
    return {
      id: group.id,
      amount: group.amount ?? null,
      percent_of_opening: group.percent_of_opening ?? group.percentOfOpening ?? null,
      row_count: sectionRows.length,
      rows: sectionRows
    }
  })

  const seen = new Set(named.map((section) => section.id))
  const orphans = [...buckets.keys()]
    .filter((id) => !seen.has(id))
    .map((id) => {
      const sectionRows = buckets.get(id)
      return { id, amount: null, percent_of_opening: null, row_count: sectionRows.length, rows: sectionRows }
    })

  return named.concat(orphans).filter((section) => section.rows.length > 0)
}

// Which group a row belongs to, for the chart-to-table jump.
export function groupIdFor(rows, rowId) {
  const row = (rows || []).find((candidate) => candidate.id === rowId)
  return row ? row.group : null
}

// The two kinds of row that are a DRIVER of the movement, as opposed to a stage
// of the method. One list, because two lists is how the chart and the "largest
// single driver" caption would come to disagree about the same row.
export const MOVER_KINDS = [ 'driver', 'consolidated' ]

// A completed run stored before EVA-315 cannot be rendered safely: its 2dp
// amounts have already lost the distinction between exact zero and tiny
// nonzero. `stale` still tells the reader to rerun it, while this gate keeps the
// incompatible headline, table and waterfall off screen.
export function presentableResult(run) {
  if (run?.status !== 'COMPLETE') return null
  return Array.isArray(run.result?.presentation_rows) ? run.result : null
}

// EVA-315: the server makes the visibility decision before currency serialisation, on
// the exact BigDecimal impact. `rows` remains the complete audit record, while
// `presentation_rows` is what a valuer sees. Runs stored before this field was
// introduced are marked stale server-side because their 2dp amounts cannot
// distinguish an exact zero from a tiny nonzero.
export function presentationRows(result) {
  return result?.presentation_rows || []
}

// Visibility has already been decided from exact server-side amounts. A tiny
// nonzero movement can legitimately arrive as "0.0" at the 2dp wire boundary,
// so the browser must not take a second opinion from the formatted value.
export function chartBars(bars) {
  return bars || []
}

// The largest movements first, by absolute size, ignoring sign — "what moved
// this valuation most" is a question about magnitude.
//
// DRIVERS ONLY. Rounding is outside the economic identity and the residual is
// by definition what no driver explains, so neither is an answer to "what moved
// it"; the passage of time is a stage of the method rather than a driver. On a
// small bridge — the stakeholder's own example is "time + rounding only" — a
// ranking over every row would nominate the rounding plug.
//
// Ties break on the row id so the order is stable across runs (general.md's
// deterministic ordering, and the same reason: without a unique tiebreaker the
// list reshuffles on its own).
export function rankedRows(rows, limit = 5) {
  return (rows || [])
    .filter((row) => MOVER_KINDS.includes(row.kind))
    .filter((row) => toNumber(row.amount) !== null)
    .sort((a, b) => {
      const delta = Math.abs(Number(b.amount)) - Math.abs(Number(a.amount))
      return delta !== 0 ? delta : String(a.id).localeCompare(String(b.id))
    })
    .slice(0, limit)
}

// How many records the resolver could not correspond, as a flat list the tab can
// render. `{ Tenant: 2 }` on both sides is TWO separate statements — two leases
// on the earlier side matched nothing later, and two later matched nothing
// earlier — so they are not netted off against each other.
export function correspondenceNotices(correspondence) {
  if (!correspondence) return []

  const notices = []
  const collect = (side, counts) => {
    Object.entries(counts || {}).forEach(([modelName, count]) => {
      if (count > 0) notices.push({ side, model: underscore(modelName), count })
    })
  }

  collect('earlier', correspondence.unmatched_earlier)
  collect('later', correspondence.unmatched_later)
  return notices
}
